import {
  MAX_GROUP_SIZE,
  MAX_JOKERS_PER_MELD,
  MAX_NUMBER,
  MIN_MELD_SIZE,
  MIN_NUMBER,
  type Meld,
  type Tile,
} from './types.ts';

export type MeldKind = 'group' | 'run';

export interface MeldValidation {
  valid: boolean;
  kind: MeldKind | null;
  /** 組合計分（百搭以被替代牌面值計算；空組或不合法時為 0） */
  score: number;
  /** 若不合法的原因描述 */
  reason?: string;
}

/**
 * 判斷單一組合是否合法，並回傳類型與分數。
 * Group: 3–4 張同數字、不同色。
 * Run:   ≥3 張同色連號，1–13 不循環。
 * 百搭可替代任一合法位置；單組最多 2 張百搭。
 */
export function validateMeld(meld: Meld): MeldValidation {
  if (meld.length < MIN_MELD_SIZE) {
    return { valid: false, kind: null, score: 0, reason: 'TOO_SHORT' };
  }
  const jokers = meld.filter((t) => t.isJoker).length;
  if (jokers > MAX_JOKERS_PER_MELD) {
    return { valid: false, kind: null, score: 0, reason: 'TOO_MANY_JOKERS' };
  }
  const asGroup = tryGroup(meld, jokers);
  if (asGroup.valid) return asGroup;
  const asRun = tryRun(meld, jokers);
  if (asRun.valid) return asRun;
  return { valid: false, kind: null, score: 0, reason: 'NEITHER_GROUP_NOR_RUN' };
}

function tryGroup(meld: Meld, jokers: number): MeldValidation {
  if (meld.length > MAX_GROUP_SIZE) {
    return { valid: false, kind: null, score: 0, reason: 'GROUP_TOO_LONG' };
  }
  const nonJokers = meld.filter((t) => !t.isJoker);
  if (nonJokers.length === 0) {
    return { valid: false, kind: null, score: 0, reason: 'ALL_JOKERS' };
  }
  const number = nonJokers[0]!.number!;
  const colors = new Set<string>();
  for (const t of nonJokers) {
    if (t.number !== number) return { valid: false, kind: null, score: 0, reason: 'GROUP_NUMBER_MISMATCH' };
    if (t.color == null) return { valid: false, kind: null, score: 0, reason: 'INVALID_TILE' };
    if (colors.has(t.color)) return { valid: false, kind: null, score: 0, reason: 'GROUP_DUPLICATE_COLOR' };
    colors.add(t.color);
  }
  // jokers 佔用剩餘顏色空位：只要沒有顏色重複，且 jokers + nonJokers ≤ 4 即合法。
  // 每張 tile（含 joker）都以 number 計分。
  const score = number * meld.length;
  return { valid: true, kind: 'group', score };
}

function tryRun(meld: Meld, jokers: number): MeldValidation {
  // 同色檢查（忽略 joker）
  const nonJokers = meld.filter((t) => !t.isJoker);
  if (nonJokers.length === 0) {
    return { valid: false, kind: null, score: 0, reason: 'ALL_JOKERS' };
  }
  const color = nonJokers[0]!.color;
  for (const t of nonJokers) {
    if (t.color !== color) return { valid: false, kind: null, score: 0, reason: 'RUN_COLOR_MISMATCH' };
  }

  // 嘗試把 jokers 填入一個 meld.length 長的連號序列，起點 start，使所有 non-joker 位置對得上。
  // start 需滿足：start ≥ 1、start + length - 1 ≤ 13。
  const length = meld.length;
  for (let start = MIN_NUMBER; start + length - 1 <= MAX_NUMBER; start++) {
    if (tryFit(meld, start, jokers)) {
      // 分數 = start..start+length-1 總和；joker 替代位置照該位的數字計分。
      let score = 0;
      for (let i = 0; i < length; i++) score += start + i;
      return { valid: true, kind: 'run', score };
    }
  }
  return { valid: false, kind: null, score: 0, reason: 'RUN_NOT_CONSECUTIVE' };
}

function tryFit(meld: Meld, start: number, _jokers: number): boolean {
  // 將 meld 視為有序：index i 對應數字 start + i。
  // 非 joker 的 tile 必須 number === start + i；joker 可填任意位置。
  for (let i = 0; i < meld.length; i++) {
    const t = meld[i]!;
    if (t.isJoker) continue;
    if (t.number !== start + i) return false;
  }
  return true;
}

/** 檢查整個桌面是否每個 meld 都合法。 */
export function validateBoard(board: readonly Meld[]): {
  valid: boolean;
  invalidIndex?: number;
  reason?: string;
} {
  for (let i = 0; i < board.length; i++) {
    const result = validateMeld(board[i]!);
    if (!result.valid) return { valid: false, invalidIndex: i, reason: result.reason };
  }
  return { valid: true };
}

/** 將一張牌的數字分數（用於手牌殘分計算）。百搭獨立計 30。 */
export function tileScore(tile: Tile, jokerValue = 30): number {
  if (tile.isJoker) return jokerValue;
  return tile.number ?? 0;
}
